import type { ApiError as ApiErrorBody } from '@greenroom/shared'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Participant token for session calls; otherwise the session cookie is used. */
  token?: string
  signal?: AbortSignal
  headers?: Record<string, string>
}

/**
 * The one way the app talks to the API. Writes without a body send none at all: the API rejects an
 * empty body that claims to be JSON.
 */
export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...opts.headers }
  if (opts.token) headers.authorization = `Bearer ${opts.token}`
  let body: BodyInit | undefined
  if (opts.body instanceof Blob || opts.body instanceof ArrayBuffer || opts.body instanceof Uint8Array) {
    body = opts.body as BodyInit
    headers['content-type'] ??= 'application/octet-stream'
  } else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body)
    headers['content-type'] = 'application/json'
  }

  let res: Response
  try {
    res = await fetch(path, { method: opts.method ?? (body ? 'POST' : 'GET'), headers, body, signal: opts.signal, credentials: 'same-origin' })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection.')
  }
  if (res.status === 204) return undefined as T
  const type = res.headers.get('content-type') ?? ''
  const data: unknown = type.includes('json') ? await res.json().catch(() => null) : await res.text()
  if (!res.ok) {
    const err = (data && typeof data === 'object' ? data : {}) as Partial<ApiErrorBody>
    throw new ApiError(res.status, err.error ?? 'error', err.message ?? `Request failed (${res.status})`, err.details)
  }
  return data as T
}

export const isApiError = (err: unknown, status?: number): err is ApiError =>
  err instanceof ApiError && (status === undefined || err.status === status)

/** A message fit for a toast, whatever was thrown. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong'
}

/** Per-field messages of a 400, keyed by the body field they belong to. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!isApiError(err, 400) || !Array.isArray(err.details)) return {}
  const out: Record<string, string> = {}
  // the API reports JSON pointers: `/email`
  for (const d of err.details as { path?: string; message?: string }[]) {
    const key = d.path?.split('/')[1]
    if (key && d.message && !out[key]) out[key] = d.message
  }
  return out
}
